export type Listener<T = unknown> = (data: T) => void

export default class EventDispatcher {
  private listeners = new Map<string, Set<Listener>>()

  public addEventListener<T = unknown>(type: string, listener: Listener<T>): void {
    const set = this.listeners.get(type) ?? new Set<Listener>()
    set.add(listener as Listener) // Set 自动去重
    this.listeners.set(type, set)
  }

  // 一次性监听，触发后自动移除，避免监听泄漏
  public once<T = unknown>(type: string, listener: Listener<T>): void {
    const wrap: Listener<T> = (data) => {
      try {
        listener(data)
      } finally {
        this.removeEventListener(type, wrap)
      }
    }
    this.addEventListener(type, wrap)
  }

  public removeEventListener<T = unknown>(type: string, listener: Listener<T>): void {
    const set = this.listeners.get(type)
    if (!set) return
    set.delete(listener as Listener)
    if (set.size === 0) this.listeners.delete(type)
  }

  public removeAllListeners(type?: string): void {
    if (type) {
      this.listeners.delete(type)
    } else {
      this.listeners.clear()
    }
  }

  public listenerCount(type: string): number {
    return this.listeners.get(type)?.size || 0
  }

  // 用快照数组迭代，避免遍历中被 once/off 修改导致迭代器失效
  public dispatchEvent<T = unknown>(type: string, data: T): void {
    const set = this.listeners.get(type)
    if (!set || set.size === 0) return
    const snapshot = Array.from(set)
    for (const listener of snapshot) {
      try {
        ;(listener as Listener<T>)(data)
      } catch (err) {
        // 单个监听抛错不影响其他监听执行
        console.error(`Error in listener for '${type}':`, err)
      }
    }
  }

  // 轻量辅助：判断是否存在监听（常用于性能优化或调试）
  public hasListeners(type: string): boolean {
    return !!this.listeners.get(type)?.size
  }
}
